import type { AnchoredRow } from '../types/anchoredRowTypes'
import type { LedgerSafeUnassignedFragment } from './ledgerSafeBoundaryReconcile'
import type { LedgerSafeSequenceAudit } from './ledgerSafeSequenceAudit'

export type LedgerSafeFragmentAnchorSource = 'sequence-audit' | 'manual'

export interface LedgerSafeFragmentAnchorDraft {
  witnessId: string
  unitUuid: string
  unitText: string
  previousRowId: string | null
  nextRowId: string | null
  source: LedgerSafeFragmentAnchorSource
  createdAt: string
  updatedAt: string
}

export interface LedgerSafeFragmentAnchorResolution {
  fragments: LedgerSafeUnassignedFragment[]
  validAnchors: LedgerSafeFragmentAnchorDraft[]
  invalidAnchorKeys: string[]
}

export const buildLedgerSafeFragmentAnchorKey = (
  witnessId: string,
  unitUuid: string
): string => `${witnessId}::${unitUuid}`

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  return left.labelNumber - right.labelNumber
})

/**
 * Recover a reliable display locus for fragments whose immediate mapped
 * neighbours are unusable (for example, because those neighbours are
 * transposed in ledger order). The sequence audit already reduces placements
 * to an in-order backbone, so its locus is the safer automatic fallback.
 *
 * This changes only the fragment's pending placement context. It never maps a
 * unit into a row and never mutates the stored collation.
 */
export const inferLedgerSafeFragmentAnchorsFromSequenceAudit = ({
  fragments,
  sequenceAudit,
  rows
}: {
  fragments: LedgerSafeUnassignedFragment[]
  sequenceAudit: LedgerSafeSequenceAudit | null | undefined
  rows: AnchoredRow[]
}): LedgerSafeUnassignedFragment[] => {
  if (!sequenceAudit || fragments.length === 0) {
    return fragments
  }

  const sortedRows = sortRows(rows)
  const rowIndexById = new Map(sortedRows.map((row, index) => [row.id, index]))
  const locusByUnitUuid = new Map(sequenceAudit.unplacedUnits.map(unit => [
    unit.unitUuid,
    {
      previousRowId: unit.belongsAfterRowId,
      nextRowId: unit.belongsBeforeRowId
    }
  ]))

  return fragments.map(fragment => {
    if (
      fragment.witnessId !== sequenceAudit.witnessId
      || fragment.previousRowId
      || fragment.nextRowId
    ) {
      return fragment
    }

    const locus = locusByUnitUuid.get(fragment.unitUuid)
    if (!locus || (!locus.previousRowId && !locus.nextRowId)) {
      return fragment
    }

    const previousIndex = locus.previousRowId
      ? rowIndexById.get(locus.previousRowId)
      : undefined
    const nextIndex = locus.nextRowId
      ? rowIndexById.get(locus.nextRowId)
      : undefined
    const rowsExist = (
      (!locus.previousRowId || previousIndex !== undefined)
      && (!locus.nextRowId || nextIndex !== undefined)
    )
    const isForward = (
      previousIndex === undefined
      || nextIndex === undefined
      || previousIndex < nextIndex
    )
    if (!rowsExist || !isForward) {
      return fragment
    }

    return {
      ...fragment,
      previousRowId: locus.previousRowId,
      nextRowId: locus.nextRowId
    }
  })
}

export const buildLedgerSafeFragmentAnchorDraft = ({
  fragment,
  previousRowId,
  nextRowId,
  source,
  existing,
  timestamp = new Date().toISOString()
}: {
  fragment: LedgerSafeUnassignedFragment
  previousRowId: string | null
  nextRowId: string | null
  source: LedgerSafeFragmentAnchorSource
  existing?: LedgerSafeFragmentAnchorDraft | null
  timestamp?: string
}): LedgerSafeFragmentAnchorDraft => ({
  witnessId: fragment.witnessId,
  unitUuid: fragment.unitUuid,
  unitText: fragment.text,
  previousRowId,
  nextRowId,
  source,
  createdAt: existing?.createdAt || timestamp,
  updatedAt: timestamp
})

export const upsertLedgerSafeFragmentAnchor = (
  anchors: LedgerSafeFragmentAnchorDraft[],
  anchor: LedgerSafeFragmentAnchorDraft
): LedgerSafeFragmentAnchorDraft[] => {
  const key = buildLedgerSafeFragmentAnchorKey(anchor.witnessId, anchor.unitUuid)
  return [
    ...anchors.filter(candidate => (
      buildLedgerSafeFragmentAnchorKey(candidate.witnessId, candidate.unitUuid) !== key
    )),
    anchor
  ]
}

export const removeLedgerSafeFragmentAnchors = (
  anchors: LedgerSafeFragmentAnchorDraft[],
  fragmentKeys: string[]
): LedgerSafeFragmentAnchorDraft[] => {
  const keys = new Set(fragmentKeys)
  return anchors.filter(anchor => (
    !keys.has(buildLedgerSafeFragmentAnchorKey(anchor.witnessId, anchor.unitUuid))
  ))
}

export const resolveLedgerSafeFragmentAnchors = ({
  fragments,
  anchors,
  rows
}: {
  fragments: LedgerSafeUnassignedFragment[]
  anchors: LedgerSafeFragmentAnchorDraft[]
  rows: AnchoredRow[]
}): LedgerSafeFragmentAnchorResolution => {
  const sortedRows = sortRows(rows)
  const rowIndexById = new Map(sortedRows.map((row, index) => [row.id, index]))
  const fragmentByKey = new Map(fragments.map(fragment => [
    buildLedgerSafeFragmentAnchorKey(fragment.witnessId, fragment.unitUuid),
    fragment
  ]))
  const anchorByKey = new Map(anchors.map(anchor => [
    buildLedgerSafeFragmentAnchorKey(anchor.witnessId, anchor.unitUuid),
    anchor
  ]))
  const validAnchors: LedgerSafeFragmentAnchorDraft[] = []
  const invalidAnchorKeys: string[] = []

  anchorByKey.forEach((anchor, key) => {
    const fragment = fragmentByKey.get(key)
    const previousIndex = anchor.previousRowId
      ? rowIndexById.get(anchor.previousRowId)
      : undefined
    const nextIndex = anchor.nextRowId
      ? rowIndexById.get(anchor.nextRowId)
      : undefined
    const hasBoundary = Boolean(anchor.previousRowId || anchor.nextRowId)
    const rowsExist = (
      (!anchor.previousRowId || previousIndex !== undefined)
      && (!anchor.nextRowId || nextIndex !== undefined)
    )
    const isForward = (
      previousIndex === undefined
      || nextIndex === undefined
      || previousIndex < nextIndex
    )
    const isCurrent = Boolean(
      fragment
      && fragment.text === anchor.unitText
      && hasBoundary
      && rowsExist
      && isForward
    )
    if (isCurrent) {
      validAnchors.push(anchor)
    } else {
      invalidAnchorKeys.push(key)
    }
  })

  const validAnchorByKey = new Map(validAnchors.map(anchor => [
    buildLedgerSafeFragmentAnchorKey(anchor.witnessId, anchor.unitUuid),
    anchor
  ]))
  return {
    fragments: fragments.map(fragment => {
      const anchor = validAnchorByKey.get(
        buildLedgerSafeFragmentAnchorKey(fragment.witnessId, fragment.unitUuid)
      )
      return anchor
        ? {
            ...fragment,
            previousRowId: anchor.previousRowId,
            nextRowId: anchor.nextRowId
          }
        : fragment
    }),
    validAnchors,
    invalidAnchorKeys
  }
}
